/**
 * PWA runtime: service-worker registration + install promotion.
 *
 * Imported for its side effects at the TOP of both app.js and guest.js — the
 * beforeinstallprompt listener must attach at module scope because Chrome fires
 * the event right after `load`, well before alpine:stores-ready. The Alpine
 * factory below (window.pwaInstallPrompt) backs the install-prompt overlay in
 * resources/views/partials/pwa-install-prompt.blade.php (authenticated layout
 * only, so users install their tenant-subdomain origin, not the apex login).
 *
 * The service worker (/public/sw.js) is deliberately minimal: offline fallback
 * for failed navigations, nothing else. FUTURE (Web Push): the subscribe flow
 * belongs in this module, push handlers in sw.js.
 */

const DISMISS_KEY = 'pwa-install-dismissed';

// Stashed beforeinstallprompt event; consumed by the Alpine component's install().
let deferredInstallPrompt = null;

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (event) => {
        // Suppress Chrome's mini-infobar; we surface our own button instead.
        event.preventDefault();
        deferredInstallPrompt = event;
        window.dispatchEvent(new CustomEvent('pwa:installable'));
    });

    window.addEventListener('appinstalled', () => {
        deferredInstallPrompt = null;
        window.dispatchEvent(new CustomEvent('pwa:installed'));
    });
}

export function isStandalone(win = window) {
    return Boolean(
        win.matchMedia?.('(display-mode: standalone)')?.matches ||
            win.navigator?.standalone // iOS Safari's non-standard flag
    );
}

// iPadOS ≥ 13 masquerades as "MacIntel" but is the only Mac with a touchscreen.
// Chrome/Firefox/Edge on iOS are excluded: they can't Add to Home Screen.
export function isIosSafari(ua = navigator.userAgent, maxTouchPoints = navigator.maxTouchPoints ?? 0, platform = navigator.platform ?? '') {
    const isIosDevice = /iP(hone|ad|od)/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);
    const isOtherIosBrowser = /CriOS|FxiOS|EdgiOS/.test(ua);

    return isIosDevice && !isOtherIosBrowser;
}

export function isDismissed(storage = localStorage) {
    try {
        return storage.getItem(DISMISS_KEY) === '1';
    } catch {
        return false; // storage blocked → just show the prompt
    }
}

export function markDismissed(storage = localStorage) {
    try {
        storage.setItem(DISMISS_KEY, '1');
    } catch {
        // storage blocked → dismissal lasts for this page view only
    }
}

/**
 * Pure decision: which prompt (if any) to show.
 * @returns {'native'|'ios'|null}
 */
export function shouldShowPrompt({ standalone, dismissed, installable, iosSafari }) {
    if (standalone || dismissed) {
        return null;
    }
    if (installable) {
        return 'native'; // Chrome/Android: one-tap install via the stashed event
    }
    if (iosSafari) {
        return 'ios'; // Safari has no prompt API: show Add to Home Screen steps
    }

    return null;
}

if (typeof window !== 'undefined') {
    // Alpine factory for the install-prompt partial (window-factory pattern,
    // same as the other x-data components in this codebase).
    window.pwaInstallPrompt = () => ({
        mode: null, // 'native' | 'ios' | null (hidden)

        init() {
            this.refresh();
            window.addEventListener('pwa:installable', () => this.refresh());
            window.addEventListener('pwa:installed', () => (this.mode = null));
        },

        refresh() {
            this.mode = shouldShowPrompt({
                standalone: isStandalone(),
                dismissed: isDismissed(),
                installable: deferredInstallPrompt !== null,
                iosSafari: isIosSafari(),
            });
        },

        async install() {
            if (!deferredInstallPrompt) {
                return;
            }
            deferredInstallPrompt.prompt();
            const { outcome } = await deferredInstallPrompt.userChoice;
            deferredInstallPrompt = null;
            if (outcome === 'dismissed') {
                // Declining the native dialog counts as a dismissal — don't nag.
                markDismissed();
            }
            this.mode = null;
        },

        dismiss() {
            markDismissed();
            this.mode = null;
        },
    });

    if ('serviceWorker' in navigator && window.isSecureContext) {
        // After load: registration must never compete with app startup.
        window.addEventListener('load', () => {
            navigator.serviceWorker.register('/sw.js').catch((error) => {
                console.warn('[pwa] Service worker registration failed', error);
            });
        });
    }
}
